import ProgressBar from "@/components/shared/ProgressBar";
import { formatMoney, formatNumber, percentOf, pluralize, formatDate } from "@/lib/format";

// Where the campaign is, in words: "6 days of reading left",
// "Reading has ended · Donations close Nov 6".
export function campaignStatusLine(campaign) {
  switch (campaign.state) {
    case "draft":
      return `Reading starts ${formatDate(campaign.start_at, { year: false, weekday: true })}`;
    case "active":
      return campaign.days_remaining <= 1 ? "Last day of reading" : `${campaign.days_remaining} days of reading left`;
    case "grace":
      return campaign.grace_ends_at
        ? `Reading has ended · Donations close ${formatDate(campaign.grace_ends_at, { year: false })}`
        : "Reading has ended · Donations still open";
    default:
      return "Finished";
  }
}

/**
 * The whole school's progress: money raised against the goal, reading
 * against the goal, and where the campaign is. Each figure appears once.
 */
export default function SchoolProgress({ school, campaign, raisedCents, minutesLogged, count, countWord = "donation", title }) {
  return (
    <section className="space-y-4">
      <h2 className="font-display text-xl font-semibold text-foreground">{title || `How ${school.name} is doing`}</h2>
      <div className="space-y-4 rounded-xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <p>
            <span className="font-display text-3xl font-semibold text-primary font-tabular">{formatMoney(raisedCents)}</span>
            <span className="ml-1.5 text-sm text-muted-foreground">
              raised{campaign.money_goal_cents ? ` of ${formatMoney(campaign.money_goal_cents)}` : ""}
            </span>
          </p>
          <p className="text-sm text-muted-foreground">
            {count != null && `${pluralize(count, countWord)} · `}{campaignStatusLine(campaign)}
          </p>
        </div>
        {campaign.money_goal_cents && (
          <ProgressBar pct={percentOf(raisedCents, campaign.money_goal_cents)} size="lg" label="Fundraising goal" detail={`${percentOf(raisedCents, campaign.money_goal_cents)}%`} />
        )}
        {campaign.minute_goal ? (
          <ProgressBar
            label="Reading goal"
            detail={`${formatNumber(minutesLogged)} / ${formatNumber(campaign.minute_goal)} min`}
            pct={percentOf(minutesLogged, campaign.minute_goal)}
            barColor="bg-accent"
          />
        ) : (
          <p className="text-sm text-muted-foreground">{formatNumber(minutesLogged)} minutes read so far.</p>
        )}
      </div>
    </section>
  );
}
